import { createSlice } from '@reduxjs/toolkit';
import {
  getAssignmentRule,
  getAssignmentRules,
  getAssignmentRulesSummary,
  getAssignmentRulesOptionsService,
  getWebcontactUpgradePending,
  updateAssigmentRulesOrder,
  createAssignmentRuleService,
  updateAssignmentRuleService,
  deleteAssingmentRuleService,
  requestWebcontactRulesUpgrade,
  getWebcontactOrigins,
  getAgentsForWebcontact,
  getLeadStatuses,
  getCompanyBranches,
  getCompanyTeams,
  getCustomTags,
  getAssignmentLimits,
  updateAssignmentLimits,
} from './services';

import initialState from './initialState';
import { ASSIGNMENT_RULE_INTERNAL_NAMES } from '@/components/AutomatizationRules/RuleForm/constants';

const sortOutOfOfficeFirst = (rulesList) => {
  const outOfOfficeRules = rulesList.filter(
    (rule) =>
      rule?.internal_name === ASSIGNMENT_RULE_INTERNAL_NAMES.OUT_OF_OFFICE
  );
  if (outOfOfficeRules.length === 0) return rulesList;

  const rest = rulesList.filter(
    (rule) =>
      rule?.internal_name !== ASSIGNMENT_RULE_INTERNAL_NAMES.OUT_OF_OFFICE
  );
  return [...outOfOfficeRules, ...rest];
};

/**
 * Normalizes assignment rule actions so that "paused" state lives in a single
 * source of truth depending on distribution mode:
 * - Random (has `chances`): `chances[i].paused` only. Legacy `paused_agent_ids`
 *   is merged into each chance's `paused` (if missing) and then stripped.
 * - Round-robin (no `chances`): `paused_agent_ids` only (untouched).
 */
const normalizeAssignmentRuleChances = (ruleData) => {
  if (!ruleData?.actions?.length) return ruleData;
  return {
    ...ruleData,
    actions: ruleData.actions.map((action) => {
      const chances = action?.extra_data?.chances;
      if (!Array.isArray(chances) || chances.length === 0) return action;
      const legacyPausedSet = new Set(
        Array.isArray(action.extra_data?.paused_agent_ids)
          ? action.extra_data.paused_agent_ids
          : []
      );
      const { paused_agent_ids: _ignored, ...restExtra } =
        action.extra_data || {};
      return {
        ...action,
        extra_data: {
          ...restExtra,
          chances: chances.map((chance) => ({
            ...chance,
            paused:
              typeof chance?.paused === 'boolean'
                ? chance.paused
                : legacyPausedSet.has(chance.id),
          })),
        },
      };
    }),
  };
};

const setLabels = (rulesList) => {
  const orderedRules = sortOutOfOfficeFirst(rulesList);
  let firstActiveFound = false;

  return orderedRules.map((rule) => {
    let labelKey = 'InactiveRule';

    if (rule.active) {
      if (!firstActiveFound) {
        labelKey = 'FirstRuleToCheck';
        firstActiveFound = true;
      } else {
        labelKey = 'NextRuleToCheck';
      }
    }

    rule.label = labelKey;
    return rule;
  });
};

const restructureGeneralRules = (rules) => {
  const generalRules = rules.flatMap((rule) => {
    if (
      rule.name === 'Inquiries from closed contacts' &&
      rule.actions[0].extra_data.has_time
    ) {
      return [
        {
          ...rule,
          name: rule.actions[0].extra_data.less.name,
          active: rule.actions[0].extra_data.less.active,
          time_rule_key: 'less',
          actions: [
            {
              extra_data: {
                options_key: 'INQUIRIES_FROM_CLOSED_CONTACTS',
                has_time: true,
                time_value: rule.actions[0].extra_data.time_value,
                time_unit: rule.actions[0].extra_data.time_unit,
                time_label: rule.actions[0].extra_data.time_label,
              },
              value: rule.actions[0].extra_data.less.action,
            },
          ],
        },
        {
          ...rule,
          name: rule.actions[0].extra_data.more.name,
          active: rule.actions[0].extra_data.more.active,
          time_rule_key: 'more',
          actions: [
            {
              extra_data: {
                options_key: 'INQUIRIES_FROM_CLOSED_CONTACTS',
                has_time: true,
                time_value: rule.actions[0].extra_data.time_value,
                time_unit: rule.actions[0].extra_data.time_unit,
                time_label: rule.actions[0].extra_data.time_label,
              },
              value: rule.actions[0].extra_data.more.action,
            },
          ],
        },
      ];
    }

    return [rule];
  });
  return generalRules;
};

const countActiveRules = (rules1, rules2) => {
  const countActiveGeneralRules = rules1.filter((rule) => rule.active).length;
  const countActiveCustomRules = rules2.filter((rule) => rule.active).length;
  return {
    activeGeneralRulesCount: countActiveGeneralRules,
    activeAssignmentRulesCount:
      countActiveGeneralRules + countActiveCustomRules,
  };
};

export const WebcontactSlice = createSlice({
  name: 'webcontact',
  initialState,
  reducers: {
    setAssignmentRulesShowInactive: (state, action) => {
      state.assignmentRulesShowInactive = action.payload;
    },
    updateAssignmentGeneralRule: (state, action) => {
      const updatedRule = action.payload;
      const index = state.generalRules.findIndex(
        (rule) => rule.id === updatedRule.id
      );
      if (index !== -1) {
        state.generalRules[index] = updatedRule;
      }

      // update counters
      const { activeGeneralRulesCount, activeAssignmentRulesCount } =
        countActiveRules(state.generalRules, state.assignmentRules);
      state.activeGeneralRulesCount = activeGeneralRulesCount;
      state.activeAssignmentRulesCount = activeAssignmentRulesCount;
      state.assignmentRulesSummary.actives = activeAssignmentRulesCount;
    },
    updateAssignmentRule: (state, action) => {
      const updatedRule = action.payload;
      const index = state.assignmentRules.findIndex(
        (rule) => rule.id === updatedRule.id
      );
      if (index !== -1) {
        state.assignmentRules[index] = updatedRule;
      }

      state.assignmentRules = setLabels(state.assignmentRules);

      // update counters
      const { activeGeneralRulesCount, activeAssignmentRulesCount } =
        countActiveRules(state.generalRules, state.assignmentRules);
      state.activeGeneralRulesCount = activeGeneralRulesCount;
      state.activeAssignmentRulesCount = activeAssignmentRulesCount;
      state.assignmentRulesSummary.actives = activeAssignmentRulesCount;
    },
    updateUnfoldedRule: (state, action) => {
      const { ruleId, timeRuleKey, updates } = action.payload;

      const ruleIndex = state.generalRules.findIndex(
        (rule) => rule.id === ruleId && rule.time_rule_key === timeRuleKey
      );

      if (ruleIndex !== -1) {
        state.generalRules[ruleIndex] = {
          ...state.generalRules[ruleIndex],
          ...updates,
        };
      }

      // Update the backup rule for future API calls
      if (state.thirdRuleBackup && state.thirdRuleBackup.id === ruleId) {
        const apiUpdates = {};

        if (updates.actions && updates.actions[0] && updates.actions[0].value) {
          apiUpdates.action = updates.actions[0].value;
        } else if (updates.active !== undefined) {
          apiUpdates.active = updates.active;
        }

        state.thirdRuleBackup = {
          ...state.thirdRuleBackup,
          actions: [
            {
              ...state.thirdRuleBackup.actions[0],
              extra_data: {
                ...state.thirdRuleBackup.actions[0].extra_data,
                [timeRuleKey]: {
                  ...state.thirdRuleBackup.actions[0].extra_data[timeRuleKey],
                  ...apiUpdates,
                },
              },
            },
          ],
        };
      }

      // update counters
      const { activeGeneralRulesCount, activeAssignmentRulesCount } =
        countActiveRules(state.generalRules, state.assignmentRules);
      state.activeGeneralRulesCount = activeGeneralRulesCount;
      state.activeAssignmentRulesCount = activeAssignmentRulesCount;
      state.assignmentRulesSummary.actives = activeAssignmentRulesCount;
    },
    updateThirdRuleFromServer: (state, action) => {
      const updatedRule = action.payload;
      state.thirdRuleBackup = updatedRule;

      state.generalRules = state.generalRules.filter(
        (rule) => rule.id !== updatedRule.id
      );

      const reconstructedRules = restructureGeneralRules([updatedRule]);
      state.generalRules = [...state.generalRules, ...reconstructedRules];

      const activeAssignmentRulesCount = countActiveRules(
        state.generalRules,
        state.assignmentRules
      );
      state.activeAssignmentRulesCount = activeAssignmentRulesCount;
    },
    setAssignmentRuleFormData: (state, action) => {
      const data = action.payload;
      if (typeof data === 'function') {
        state.assignmentRuleForm = data(state.assignmentRuleForm);
      } else {
        state.assignmentRuleForm = { ...state.assignmentRuleForm, ...data };
      }
    },
    resetAssignmentRuleForm: (state) => {
      state.assignmentRuleForm = {
        ...initialState.assignmentRuleForm,
        attributeValidationErrors: {},
      };
    },
    clearEditingRuleData: (state) => {
      state.editingRuleData = null;
    },
    setAssignmentAttributeValidationError: (state, action) => {
      const { attributeId, hasError } = action.payload;
      if (hasError) {
        state.assignmentRuleForm.attributeValidationErrors[attributeId] = true;
      } else {
        delete state.assignmentRuleForm.attributeValidationErrors[attributeId];
      }
      // Update global validation state based on remaining errors
      state.assignmentRuleForm.hasValidationErrors =
        Object.keys(state.assignmentRuleForm.attributeValidationErrors).length >
        0;
    },
    clearAssignmentAttributeValidationErrors: (state, action) => {
      const { attributeIds } = action.payload;
      attributeIds.forEach((id) => {
        delete state.assignmentRuleForm.attributeValidationErrors[id];
      });
      // Update global validation state
      state.assignmentRuleForm.hasValidationErrors =
        Object.keys(state.assignmentRuleForm.attributeValidationErrors).length >
        0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAssignmentRule.pending, (state) => {
        state.loadingGetAssignmentRule = true;
        state.errorGetAssignmentRule = false;
        // Avoid showing stale rule data while refetching (e.g. after save + back
        // to list + edit again). Otherwise OOF local state hydrates once from old
        // actions and never updates when the request completes.
        state.editingRuleData = null;
      })
      .addCase(getAssignmentRule.fulfilled, (state, action) => {
        state.editingRuleData = normalizeAssignmentRuleChances(action.payload);
        state.loadingGetAssignmentRule = false;
        state.errorGetAssignmentRule = false;
      })
      .addCase(getAssignmentRule.rejected, (state) => {
        state.loadingGetAssignmentRule = false;
        state.errorGetAssignmentRule = true;
      })
      .addCase(getWebcontactOrigins.pending, (state) => {
        state.loadingGetOrigins = true;
        state.errorGetOrigins = false;
      })
      .addCase(getWebcontactOrigins.fulfilled, (state, action) => {
        const list = Array.isArray(action.payload?.origins)
          ? action.payload.origins
          : [];
        state.origins = list;
        state.hasCombinedOperationPortal =
          action.payload?.has_combined_operation_portal === true ||
          list.some((origin) => origin.supports_combined_operation === true);
        state.loadingGetOrigins = false;
        state.errorGetOrigins = false;
      })
      .addCase(getWebcontactOrigins.rejected, (state) => {
        state.loadingGetOrigins = false;
        state.errorGetOrigins = true;
      })
      .addCase(getAssignmentRules.pending, (state) => {
        state.loadingGetAssignmentRules = true;
        state.errorGetAssignmentRules = false;
      })
      .addCase(getAssignmentRules.fulfilled, (state, action) => {
        const [thirdRuleBackup] = action.payload.filter(
          (rule) =>
            rule.is_default === true &&
            rule.actions[0].extra_data.options_key ===
              'INQUIRIES_FROM_CLOSED_CONTACTS'
        );
        state.thirdRuleBackup = thirdRuleBackup;

        const filteredGeneralRules = action.payload.filter((rule) =>
          [
            'new_inquiries_rule',
            'being_handled_contacts_rule',
            'closed_contacts_rule',
          ].includes(rule.internal_name)
        );
        state.generalRules = restructureGeneralRules(filteredGeneralRules);

        state.incompleteAssignmentRules = action.payload
          .filter((rule) =>
            state.assignmentRulesOptions?.incomplete_rules?.includes(
              rule.internal_name
            )
          )
          .map((rule) => ({ ...rule, label: 'InactiveRule' }));
        state.incompleteAssignmentRulesIds =
          state.incompleteAssignmentRules.map((rule) => rule.id);

        const filteredAssignmentRules = action.payload.filter(
          (rule) =>
            rule.is_default === false &&
            !state.incompleteAssignmentRulesIds.includes(rule.id)
        );

        state.assignmentRules = setLabels(filteredAssignmentRules);

        // update counters
        const { activeGeneralRulesCount, activeAssignmentRulesCount } =
          countActiveRules(state.generalRules, state.assignmentRules);
        state.activeGeneralRulesCount = activeGeneralRulesCount;
        state.activeAssignmentRulesCount = activeAssignmentRulesCount;

        state.loadingGetAssignmentRules = false;
        state.errorGetAssignmentRules = false;
      })
      .addCase(getAssignmentRules.rejected, (state) => {
        state.loadingGetAssignmentRules = false;
        state.errorGetAssignmentRules = true;
      })
      .addCase(getAssignmentRulesSummary.pending, (state) => {
        state.loadingGetAssignmentRulesSummary = true;
        state.errorGetAssignmentRulesSummary = false;
      })
      .addCase(getAssignmentRulesSummary.fulfilled, (state, action) => {
        state.assignmentRulesSummary = action.payload;
        state.loadingGetAssignmentRulesSummary = false;
        state.errorGetAssignmentRulesSummary = false;
      })
      .addCase(getAssignmentRulesSummary.rejected, (state) => {
        state.loadingGetAssignmentRulesSummary = false;
        state.errorGetAssignmentRulesSummary = true;
      })
      .addCase(getWebcontactUpgradePending.pending, (state) => {
        state.loadingGetUpgradePending = true;
        state.errorGetUpgradePending = false;
      })
      .addCase(getWebcontactUpgradePending.fulfilled, (state, action) => {
        state.upgradePending = action.payload?.leads_assignment_rules || false;
        state.loadingGetUpgradePending = false;
        state.errorGetUpgradePending = false;
      })
      .addCase(getWebcontactUpgradePending.rejected, (state) => {
        state.loadingGetUpgradePending = false;
        state.errorGetUpgradePending = true;
      })
      .addCase(requestWebcontactRulesUpgrade.pending, (state) => {
        state.loadingRequestUpgrade = true;
        state.errorRequestUpgrade = false;
      })
      .addCase(requestWebcontactRulesUpgrade.fulfilled, (state) => {
        state.upgradePending = true;
        state.loadingRequestUpgrade = false;
        state.errorRequestUpgrade = false;
      })
      .addCase(requestWebcontactRulesUpgrade.rejected, (state) => {
        state.loadingRequestUpgrade = false;
        state.errorRequestUpgrade = true;
      })
      .addCase(getAssignmentRulesOptionsService.pending, (state) => {
        state.loadingGetAssignmentRulesOptions = true;
        state.errorGetAssignmentRulesOptions = false;
      })
      .addCase(getAssignmentRulesOptionsService.fulfilled, (state, action) => {
        state.assignmentRulesOptions = action.payload;
        state.loadingGetAssignmentRulesOptions = false;
        state.errorGetAssignmentRulesOptions = false;
      })
      .addCase(getAssignmentRulesOptionsService.rejected, (state) => {
        state.loadingGetAssignmentRulesOptions = false;
        state.errorGetAssignmentRulesOptions = true;
      })
      .addCase(updateAssigmentRulesOrder.pending, (state) => {
        state.updatingAssignmentRulesOrder = true;
        state.errorUpdatingAssignmentRulesOrder = false;
      })
      .addCase(updateAssigmentRulesOrder.fulfilled, (state, action) => {
        const idsArray = action.meta.arg;
        const reorderedRules = idsArray
          .filter(
            (ruleId) => !state.incompleteAssignmentRulesIds.includes(ruleId)
          )
          .map((ruleId) =>
            state.assignmentRules.find((rule) => rule.id === ruleId)
          );
        state.assignmentRules = setLabels(reorderedRules);
        state.updatingAssignmentRulesOrder = false;
        state.errorUpdatingAssignmentRulesOrder = false;
      })
      .addCase(updateAssigmentRulesOrder.rejected, (state) => {
        state.updatingAssignmentRulesOrder = false;
        state.errorUpdatingAssignmentRulesOrder = true;
      })
      .addCase(updateAssignmentRuleService.pending, (state) => {
        state.updatingAssignmentRule = true;
        state.errorUpdatingAssignmentRule = false;
      })
      .addCase(updateAssignmentRuleService.fulfilled, (state, action) => {
        state.updatingAssignmentRule = false;
        state.errorUpdatingAssignmentRule = false;

        if (
          action.payload?.actions?.[0]?.extra_data?.options_key ===
          'INQUIRIES_FROM_CLOSED_CONTACTS'
        ) {
          WebcontactSlice.caseReducers.updateThirdRuleFromServer(state, action);
        }

        const { activeGeneralRulesCount, activeAssignmentRulesCount } =
          countActiveRules(state.generalRules, state.assignmentRules);
        state.activeGeneralRulesCount = activeGeneralRulesCount;
        state.activeAssignmentRulesCount = activeAssignmentRulesCount;
      })
      .addCase(updateAssignmentRuleService.rejected, (state) => {
        state.updatingAssignmentRule = false;
        state.errorUpdatingAssignmentRule = true;
      })
      .addCase(createAssignmentRuleService.pending, (state) => {
        state.loadingCreateAssignmentRule = true;
        state.errorCreateAssignmentRule = false;
      })
      .addCase(createAssignmentRuleService.fulfilled, (state) => {
        state.loadingCreateAssignmentRule = false;
        state.errorCreateAssignmentRule = false;
      })
      .addCase(createAssignmentRuleService.rejected, (state) => {
        state.loadingCreateAssignmentRule = false;
        state.errorCreateAssignmentRule = true;
      })
      .addCase(deleteAssingmentRuleService.pending, (state) => {
        state.loadingDeleteRule = true;
        state.errorDeleteRule = false;
      })
      .addCase(deleteAssingmentRuleService.fulfilled, (state, action) => {
        const deletedRuleId = action.meta.arg;
        state.assignmentRules = setLabels(
          state.assignmentRules.filter((rule) => rule.id !== deletedRuleId)
        );
        // Do not adjust summary locally; it will be refreshed by thunk

        state.loadingDeleteRule = false;
        state.errorDeleteRule = false;
      })
      .addCase(deleteAssingmentRuleService.rejected, (state) => {
        state.loadingDeleteRule = false;
        state.errorDeleteRule = true;
      })
      .addCase(getAgentsForWebcontact.pending, (state) => {
        state.loadingGetAgents = true;
        state.errorGetAgents = false;
      })
      .addCase(getAgentsForWebcontact.fulfilled, (state, action) => {
        state.agents = action.payload.users;
        state.loadingGetAgents = false;
        state.errorGetAgents = false;
      })
      .addCase(getAgentsForWebcontact.rejected, (state) => {
        state.loadingGetAgents = false;
        state.errorGetAgents = true;
      })
      .addCase(getLeadStatuses.pending, (state) => {
        state.loadingGetLeadStatuses = true;
        state.errorGetLeadStatuses = false;
      })
      .addCase(getLeadStatuses.fulfilled, (state, action) => {
        const list = Array.isArray(action.payload) ? action.payload : [];
        state.leadStatuses = list;
        state.loadingGetLeadStatuses = false;
        state.errorGetLeadStatuses = false;
      })
      .addCase(getLeadStatuses.rejected, (state) => {
        state.loadingGetLeadStatuses = false;
        state.errorGetLeadStatuses = true;
      })
      .addCase(getCompanyBranches.pending, (state) => {
        state.loadingGetBranches = true;
        state.errorGetBranches = false;
      })
      .addCase(getCompanyBranches.fulfilled, (state, action) => {
        const { branches } = action.payload;
        state.branches = branches || [];
        state.loadingGetBranches = false;
        state.errorGetBranches = false;
      })
      .addCase(getCompanyBranches.rejected, (state) => {
        state.loadingGetBranches = false;
        state.errorGetBranches = true;
      })
      .addCase(getCompanyTeams.pending, (state) => {
        state.loadingGetTeams = true;
        state.errorGetTeams = false;
      })
      .addCase(getCompanyTeams.fulfilled, (state, action) => {
        // `listTeams` paginates internally and resolves to `{ results }`,
        // unlike `getCompanyBranches` which resolves to `{ branches }`.
        state.teams = action.payload?.results || [];
        state.loadingGetTeams = false;
        state.errorGetTeams = false;
      })
      .addCase(getCompanyTeams.rejected, (state) => {
        state.loadingGetTeams = false;
        state.errorGetTeams = true;
      })
      .addCase(getCustomTags.pending, (state) => {
        state.loadingGetCustomTags = true;
        state.errorGetCustomTags = false;
      })
      .addCase(getCustomTags.fulfilled, (state, action) => {
        const { grouped_tags } = action.payload;

        // Flatten grouped tags into a single array
        const withoutGroupTags = grouped_tags?.without_group || [];
        const groupedTags = (grouped_tags?.groups || []).flatMap(
          (group) => group.tags || []
        );

        // Build a map from tag id to group name to display in UI lists
        const groupMap = {};
        (grouped_tags?.groups || []).forEach((group) => {
          const groupName = group?.name;
          (group?.tags || []).forEach((tag) => {
            groupMap[tag.id] = groupName;
          });
        });

        state.customTags = [...withoutGroupTags, ...groupedTags];
        state.customTagsGroupMap = groupMap;
        state.loadingGetCustomTags = false;
        state.errorGetCustomTags = false;
      })
      .addCase(getCustomTags.rejected, (state) => {
        state.loadingGetCustomTags = false;
        state.errorGetCustomTags = true;
      })
      .addCase(getAssignmentLimits.pending, (state) => {
        state.loadingGetAssignmentLimits = true;
        state.errorGetAssignmentLimits = false;
      })
      .addCase(getAssignmentLimits.fulfilled, (state, action) => {
        state.assignmentLimits = action.payload;
        state.loadingGetAssignmentLimits = false;
      })
      .addCase(getAssignmentLimits.rejected, (state) => {
        state.loadingGetAssignmentLimits = false;
        state.errorGetAssignmentLimits = true;
      })
      .addCase(updateAssignmentLimits.pending, (state, action) => {
        state.updateAssignmentLimitsOrder.push(action.meta?.requestId);
        state.loadingUpdateAssignmentLimits = true;
        state.errorUpdateAssignmentLimits = false;
      })
      .addCase(updateAssignmentLimits.fulfilled, (state, action) => {
        const order = state.updateAssignmentLimitsOrder.indexOf(
          action.meta?.requestId
        );
        if (order > state.appliedUpdateAssignmentLimitsOrder) {
          state.assignmentLimits = action.payload;
          state.appliedUpdateAssignmentLimitsOrder = order;
        }
        if (order === state.updateAssignmentLimitsOrder.length - 1) {
          state.loadingUpdateAssignmentLimits = false;
        }
      })
      .addCase(updateAssignmentLimits.rejected, (state, action) => {
        const order = state.updateAssignmentLimitsOrder.indexOf(
          action.meta?.requestId
        );
        if (order === state.updateAssignmentLimitsOrder.length - 1) {
          state.loadingUpdateAssignmentLimits = false;
          state.errorUpdateAssignmentLimits = true;
        }
      });
  },
});

export const {
  setAssignmentRulesShowInactive,
  updateAssignmentRule,
  updateAssignmentGeneralRule,
  updateUnfoldedRule,
  updateThirdRuleFromServer,
  setAssignmentRuleFormData,
  resetAssignmentRuleForm,
  clearEditingRuleData,
  setAssignmentAttributeValidationError,
  clearAssignmentAttributeValidationErrors,
} = WebcontactSlice.actions;

export {
  getAssignmentRule,
  getAssignmentRules,
  getAssignmentRulesSummary,
  getAssignmentRulesOptionsService,
  createAssignmentRuleService,
  updateAssignmentRuleService,
  updateAssigmentRulesOrder,
  getWebcontactUpgradePending,
  requestWebcontactRulesUpgrade,
  getAgentsForWebcontact,
  getLeadStatuses,
  getCompanyBranches,
  getCompanyTeams,
  getAssignmentLimits,
  updateAssignmentLimits,
} from './services';

export const selectWebcontactData = (state) => state.WebcontactSlice;

/**
 * Paused agents for the assignment rule form for a contact status.
 * - Random with balancing (has `chances`): derived from `chances[i].paused`.
 * - Round-robin (no `chances`): read from `extra_data.paused_agent_ids`.
 */
export const selectPausedAgentIdsForAssignmentContactStatus =
  (contactStatus) => (state) => {
    const extra =
      state.WebcontactSlice?.assignmentRuleForm?.selectedActions?.[
        contactStatus
      ]?.extra_data;
    if (!extra) return [];
    if (extra.chances?.length) {
      return extra.chances
        .filter((chance) => chance?.paused)
        .map((chance) => chance.id);
    }
    return extra.paused_agent_ids ?? [];
  };

/** Map of `new_contact` / `existing_contact` → paused agent id lists (see selector above). */
export const selectAssignmentRulePausedAgentIdsMap = (state) => ({
  new_contact:
    selectPausedAgentIdsForAssignmentContactStatus('new_contact')(state),
  existing_contact:
    selectPausedAgentIdsForAssignmentContactStatus('existing_contact')(state),
});

export default WebcontactSlice.reducer;
